import { lazy, ComponentType } from 'react';

const inMemoryRetries = new Set<string>();
/**
 * A wrapper around React.lazy that automatically retries importing a component
 * if it fails due to a chunk load error (common after deployments).
 * 
 * If a "Failed to fetch dynamically imported module" error occurs:
 * 1. It checks if we've already tried to reload the page for this session.
 * 2. If not, it sets a flag in sessionStorage and reloads the page.
 * 3. If yes, it throws the error to be caught by an ErrorBoundary.
 */
export const lazyRetry = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
    name: string = 'component'
) => {
    return lazy(() => {
        return factory().catch((error) => {
            // Check if the error is related to chunk loading
            const isChunkError =
                error.message.includes('Failed to fetch dynamically imported module') ||
                error.message.includes('Importing a module script failed') ||
                error.name === 'ChunkLoadError';

            if (isChunkError) {
                // storage key to prevent infinite reload loops
                const storageKey = `retry-lazy-${name}`;
                
                let hasRetried = false;
                try {
                    hasRetried = sessionStorage.getItem(storageKey) === 'true' || inMemoryRetries.has(storageKey);
                } catch (e) {
                    hasRetried = inMemoryRetries.has(storageKey);
                }

                if (!hasRetried) {
                    console.log(`Chunk load failed for ${name}, reloading page...`);
                    inMemoryRetries.add(storageKey);
                    try {
                        sessionStorage.setItem(storageKey, 'true');
                    } catch (e) {
                        // Ignore storage error, inMemoryRetries will block loop on next mount if reload doesn't happen natively
                    }
                    window.location.reload();
                    // Return a never-resolving promise to wait for reload
                    return new Promise<{ default: T }>(() => { });
                } else {
                    // Clean up key so it can work next time (e.g. if user navigates away and back)
                    // Delayed cleanup or rely on session end. 
                    // For now, let's keep it simple. If we reloaded and it STILL failed, 
                    // it's a real broken deployment, not just stale chunks.
                    console.error(`Chunk load failed for ${name} even after reload.`);
                }
            }

            // Re-throw to be caught by Error Boundary
            throw error;
        });
    });
};
